import { useUpdateEffect } from "@react-hookz/web"
import { configureScope } from "@sentry/react"
import jwtDecode from "jwt-decode"
import { createContext, useContext, useEffect, useMemo, useState } from "react"

import { useAccessToken } from "@/hooks/useAccessToken"
import { isNotNullNorUndefined, removeFieldsFromObject } from "@/utils/ts-utils"

import { NIL_UUID } from "@/domain/resources/constants"
import { type UserJsonldUserReadItem, useGetUser } from "@/networking/api"
import { IS_SENTRY_ENABLED } from "@/utils/constants"
import { useAppLanguageContext } from "../AppLanguageContext/AppLanguageContext"
import { AuthenticatedUser } from "./AuthenticatedUser"
import type { AuthenticatedUserFromJWT } from "./AuthenticatedUserFromJWT"

interface IAuthenticatedUser {
  user?: AuthenticatedUser
  userInfos?: UserJsonldUserReadItem
}

export const authenticatedUserContext = createContext<IAuthenticatedUser>({})

export default function AuthenticatedUserContextProvider(props: React.PropsWithChildren) {
  const { accessToken } = useAccessToken()
  const { setLocale } = useAppLanguageContext()

  const authenticatedUser =
    accessToken !== undefined ? jwtDecode<AuthenticatedUserFromJWT>(accessToken) : undefined

  const [user, setUser] = useState<AuthenticatedUser | undefined>(() =>
    AuthenticatedUser.createFromJWTPayload(authenticatedUser)
  )

  const { data: userInfos } = useGetUser(user?.id ?? NIL_UUID, {
    query: { enabled: isNotNullNorUndefined(user), cacheTime: 0 },
  })
  const userLocale = useMemo(() => userInfos?.setting?.locale ?? undefined, [userInfos])

  const stringifiedUsefulFields =
    authenticatedUser !== undefined
      ? JSON.stringify(removeFieldsFromObject(authenticatedUser, ["exp", "iat"]))
      : undefined

  useEffect(() => {
    if (isNotNullNorUndefined(userLocale)) setLocale(userLocale)
  })

  useUpdateEffect(() => {
    setUser(AuthenticatedUser.createFromJWTPayload(authenticatedUser))
    if (IS_SENTRY_ENABLED)
      configureScope(scope => {
        scope.setUser(authenticatedUser ? { username: authenticatedUser.username } : null)
      })
  }, [stringifiedUsefulFields]) // update user only if useful fields change

  return (
    <authenticatedUserContext.Provider value={{ user, userInfos }}>
      {props.children}
    </authenticatedUserContext.Provider>
  )
}

export const useAuthenticatedUserContext = () => useContext(authenticatedUserContext)
